'use client';

import { useEffect } from 'react';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, type FormProps } from '@sola/ark-core/molecules/form/form.tsx';

import {
  type PreferenceOption,
  PreferenceRow,
  PreferenceSection,
} from '../preference-row/preference-row.tsx';
import {
  type ContactPreferenceCategory,
  ContactPreferenceChannel,
  type ContactPreferencesFormValues,
  type OptOutChannels,
  contactPreferenceChannels,
} from './contact-preferences-form.config.ts';

const emptyOptOutChannels: OptOutChannels = new Set();

export interface AdditionalContactPreference {
  channels: readonly ContactPreferenceChannel[];
  description?: string;
  fieldName: string;
  title: string;
}

interface FormDirtyStateObserverProps {
  onDirtyChange: (isDirty: boolean) => void;
}

const FormDirtyStateObserver = ({
  onDirtyChange,
}: FormDirtyStateObserverProps) => {
  const { isDirty } = useFormState();

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  return null;
};

export interface ContactPreferencesFormProps extends Omit<
  FormProps,
  'children' | 'onSubmit'
> {
  additionalPreferences?: readonly AdditionalContactPreference[];
  categories?: readonly ContactPreferenceCategory[];
  channels?: readonly ContactPreferenceChannel[];
  defaultValues: ContactPreferencesFormValues;
  onDirtyChange?: (isDirty: boolean) => void;
  onSubmit?: (values: ContactPreferencesFormValues) => void | Promise<void>;
  optOutChannels?: OptOutChannels;
  preferenceRowClassName?: string;
}

export const ContactPreferencesForm: React.FC<ContactPreferencesFormProps> = ({
  additionalPreferences = [],
  categories,
  channels,
  defaultValues,
  onDirtyChange,
  onSubmit = () => {},
  options,
  optOutChannels = emptyOptOutChannels,
  preferenceRowClassName,
  ...formProps
}) => {
  const { t } = useTranslation(['preferences']);

  const channelOptOutTooltip: Record<ContactPreferenceChannel, string> = {
    [ContactPreferenceChannel.Email]: t('preferences:optOutTooltips.email'),
    [ContactPreferenceChannel.Text]: t('preferences:optOutTooltips.text'),
  };

  const channelOptions: Record<ContactPreferenceChannel, PreferenceOption> = {
    [ContactPreferenceChannel.Email]: {
      id: ContactPreferenceChannel.Email,
      label: t('preferences:channels.email'),
      value: ContactPreferenceChannel.Email,
    },
    [ContactPreferenceChannel.Text]: {
      id: ContactPreferenceChannel.Text,
      label: t('preferences:channels.text'),
      value: ContactPreferenceChannel.Text,
    },
  };

  const categoryFilter = categories ? new Set(categories) : undefined;
  const channelFilter = channels ? new Set(channels) : undefined;

  const preferenceRows = Object.entries(contactPreferenceChannels)
    .filter(([preference]) =>
      categoryFilter
        ? categoryFilter.has(preference as ContactPreferenceCategory)
        : true,
    )
    .map(
      ([
        preference,
        {
          channels: preferenceChannels,
          disabledChannels,
          alwaysCheckedChannels,
        },
      ]) => ({
        description: t(`preferences:${preference}.description`),
        fieldName: preference,
        options: preferenceChannels
          .filter((channel) =>
            channelFilter ? channelFilter.has(channel) : true,
          )
          .map((channel) => {
            const isAlwaysChecked =
              alwaysCheckedChannels?.includes(channel) ?? false;
            const showsOptOutState =
              optOutChannels.has(channel) && !isAlwaysChecked;

            return {
              ...channelOptions[channel],
              defaultChecked:
                defaultValues[preference as ContactPreferenceCategory]?.[
                  channel
                ],
              disabled:
                (disabledChannels?.includes(channel) ?? false) ||
                showsOptOutState,
              disabledTooltip: showsOptOutState ? (
                <p>{channelOptOutTooltip[channel]}</p>
              ) : undefined,
            };
          }),
        title: t(`preferences:${preference}.title`),
      }),
    )
    .filter(({ options: preferenceOptions }) => preferenceOptions.length > 0);

  const additionalPreferenceRows = additionalPreferences
    .map(({ channels: additionalChannels, ...additionalPreference }) => ({
      ...additionalPreference,
      options: additionalChannels
        .filter((channel) =>
          channelFilter ? channelFilter.has(channel) : true,
        )
        .map((channel) => ({
          ...channelOptions[channel],
          defaultChecked:
            defaultValues[additionalPreference.fieldName]?.[channel],
          disabled: optOutChannels.has(channel),
          disabledTooltip: optOutChannels.has(channel) ? (
            <p>{channelOptOutTooltip[channel]}</p>
          ) : undefined,
        })),
    }))
    .filter(({ options: preferenceOptions }) => preferenceOptions.length > 0);

  return (
    <Form
      {...formProps}
      onSubmit={(values) => onSubmit(values as ContactPreferencesFormValues)}
      options={{ ...options, defaultValues }}
    >
      {onDirtyChange && (
        <FormDirtyStateObserver onDirtyChange={onDirtyChange} />
      )}
      <PreferenceSection>
        {[...preferenceRows, ...additionalPreferenceRows].map(
          ({ fieldName, title, description, options: preferenceOptions }) => (
            <PreferenceRow
              key={fieldName}
              className={preferenceRowClassName}
              fieldName={fieldName}
              title={title}
              description={description}
              options={preferenceOptions}
            />
          ),
        )}
      </PreferenceSection>
    </Form>
  );
};
